import { createRouter, createWebHistory, createMemoryHistory, type RouteLocationNormalized, type RouteRecordRaw, type Router } from 'vue-router'
import LandingLayout from '@/layout/LandingLayout.vue'
import DashboardLayout from '@/layout/DashboardLayout.vue'
import WebmailLayout from '@/layout/WebmailLayout.vue'
import { useJwtStore, useVersionStore } from '@/stores'
import { JwtParser } from '@/composables'
import { isClient, safeLocalStorage } from '@/composables/useSSR'
import { recoverFromChunkError } from '@/utils/chunkError'
import { getActiveI18n, translate } from '@/i18n'
import { availableLocales, type Locale } from '@/locales'
import { routeRequiresAuth } from '@/utils/authPaths'
import { loginRouteWithRedirect } from '@/utils/loginRedirect'
import { validSearchNames } from '@/composables/useDomainSearch'
import { stashParamToken, stashQueryToken } from '@/utils/urlToken'

/**
 * Locale used when building a URL and nothing better is known.
 *
 * Deliberately 'de', matching the Accept-Language fallback in server.js. It is
 * NOT defaultLocale ('en'): that one is the i18n MESSAGE fallback, where
 * English is the right choice for an untranslated string. Using it here would
 * point a German visitor's footer at /en on any unprefixed page.
 */
export const URL_FALLBACK_LOCALE: Locale = 'de'

// Custom event for dirty form navigation interception
export interface DirtyFormNavigationEvent {
  to: RouteLocationNormalized
  from: RouteLocationNormalized
}

export const DIRTY_FORM_NAVIGATION_EVENT = 'dirty-form-navigation'

export function isProdEnv(): boolean {
  return import.meta.env.PROD
}

/**
 * Best-known locale for building a URL outside a component.
 *
 * Used by the bare-'/' redirect and as the last-resort fallback in
 * withLocaleResolution. The accessor is client-only by contract, so during SSR
 * this falls back to URL_FALLBACK_LOCALE, which is deliberately NOT the
 * message default (see that constant). That is fine here:
 * the server negotiates Accept-Language and 302s before the router runs.
 */
function resolveLocale(): Locale {
  const current = getActiveI18n()?.global.locale.value
  return availableLocales.includes(current as Locale) ? (current as Locale) : URL_FALLBACK_LOCALE
}

const routes: RouteRecordRaw[] = [
  {
    // Public pages live under an explicit locale segment so each language is
    // independently indexable. Both hreflang alternates then point at real,
    // distinct URLs instead of one URL that serves whichever language the
    // server picked.
    //
    // meta.layout MUST stay on this parent: children inherit it, and App.vue
    // renders `route.meta.layout || 'div'`. Drop it and every public page
    // silently renders unwrapped, with no error to notice.
    path: '/:locale(de|en)',
    meta: {
      layout: LandingLayout
    },
    children: [
      {
        path: '',
        name: 'landingPage',
        component: () => import('@/views/LandingView.vue'),
        meta: { title: 'IT-Service Harting' }
      },
      {
        path: 'impress',
        name: 'impress',
        component: () => import('@/components/landingPage/ImpressumPage.vue'),
        meta: { titleKey: 'common.pageTitles.impress' }
      },
      {
        path: 'privacy',
        name: 'privacy',
        component: () => import('@/components/landingPage/PrivacyPolicyPage.vue'),
        meta: { titleKey: 'common.pageTitles.privacy' }
      },
      {
        path: 'agb',
        name: 'agb',
        component: () => import('@/components/landingPage/AgbPage.vue'),
        meta: { titleKey: 'common.pageTitles.agb' }
      },
      {
        path: 'agb/print',
        name: 'agbPrint',
        component: () => import('@/components/landingPage/AgbPrintPage.vue'),
        meta: { title: 'ITSH | AGB', isPrintPage: true }
      },
      {
        path: 'nis2',
        name: 'nis2',
        component: () => import('@/components/landingPage/Nis2PolicyPage.vue'),
        meta: { titleKey: 'common.pageTitles.nis2' }
      },
      {
        // § 312k BGB requires this to be reachable without signing in.
        path: 'kuendigung',
        name: 'cancellation',
        component: () => import('@/components/landingPage/KuendigungPage.vue'),
        meta: { titleKey: 'common.pageTitles.cancellation' }
      },
      {
        path: 'widerruf',
        name: 'withdrawal',
        component: () => import('@/components/landingPage/WiderrufPage.vue'),
        meta: { titleKey: 'common.pageTitles.withdrawal' }
      },
      {
        path: 'sla',
        name: 'sla',
        component: () => import('@/components/landingPage/SlaPage.vue'),
        meta: { titleKey: 'common.pageTitles.sla' }
      },
      {
        path: 'blog',
        name: 'blog',
        // Function form: a static redirect object is resolved by vue-router
        // internally, which bypasses the wrapped push and loses the locale.
        redirect: (to) => ({ name: 'blogIndex', params: { locale: to.params.locale } }),
        children: [
          {
            path: '',
            name: 'blogIndex',
            component: () => import('@/components/landingPage/Blog/BlogIndex.vue'),
            meta: { titleKey: 'blog.pageTitle' }
          },
          {
            path: ':slug',
            name: 'blogPost',
            component: () => import('@/components/landingPage/Blog/BlogPost.vue'),
            meta: { titleKey: 'blog.pageTitle' }
          }
        ]
      },
      {
        path: 'products',
        name: 'products',
        // Function form: a static redirect object is resolved by vue-router
        // internally, which bypasses the wrapped push and loses the locale.
        redirect: (to) => ({ name: 'productsHome', params: { locale: to.params.locale } }),
        children: [
          {
            path: '',
            name: 'productsHome',
            component: () => import('@/components/landingPage/products/ProductsOverview.vue'),
            meta: { titleKey: 'common.pageTitles.products' }
          },
          {
            path: 'webhosting',
            name: 'webhosting',
            component: () =>
              import('@/components/landingPage/products/Webhosting/ProductsWebhostingConfigurator.vue'),
            meta: { titleKey: 'common.pageTitles.webhosting' }
          },
          {
            path: 'kubernetes',
            name: 'kubernetes',
            component: () =>
              import('@/components/landingPage/products/Kubernetes/KubernetesBase.vue'),
            meta: { titleKey: 'common.pageTitles.kubernetes' }
          },
          {
            path: 'vserver',
            name: 'vserver',
            component: () =>
              import('@/components/landingPage/products/VServer/VServerBase.vue'),
            meta: { title: 'ITSH | VServer' }
          },
          {
            path: 'vserver-compare',
            name: 'vserverCompare',
            component: () =>
              import('@/components/landingPage/products/VServer/VServerCompare.vue'),
            meta: { titleKey: 'common.pageTitles.vserverCompare' }
          },
          {
            path: 'object-storage',
            name: 'objectStorage',
            component: () =>
              import('@/components/landingPage/products/ObjectStorage/ObjectStorageBase.vue'),
            meta: { titleKey: 'common.pageTitles.objectStorage' }
          },
          {
            path: 'email',
            name: 'emailHosting',
            component: () =>
              import('@/components/landingPage/products/Email/EmailBase.vue'),
            meta: { title: 'ITSH | E-Mail Hosting' }
          },
          {
            path: 'pricing-calculator',
            name: 'pricingCalculator',
            component: () =>
              import('@/components/landingPage/PricingCalculator/PricingCalculatorPage.vue'),
            meta: { titleKey: 'common.pageTitles.pricingCalculator' }
          },
          {
            path: 'domains',
            name: 'domains',
            // Function form: a static redirect object is resolved by vue-router
        // internally, which bypasses the wrapped push and loses the locale.
        redirect: (to) => ({ name: 'domainsSearch', params: { locale: to.params.locale } }),
            component: () =>
              import('@/components/landingPage/products/domains/DomainsPage.vue'),
            meta: { title: 'ITSH | Domains' },
            children: [
              {
                path: 'search',
                name: 'domainsSearch',
                component: () =>
                  import(
                    '@/components/landingPage/products/domains/DomainCheck/DomainCheckPage.vue'
                  ),
                meta: { titleKey: 'common.pageTitles.domainSearch' }
              },
              {
                path: 'tlds',
                name: 'domainsTlds',
                component: () =>
                  import(
                    '@/components/landingPage/products/domains/TLDCatalog/TLDCatalogPage.vue'
                  ),
                meta: { titleKey: 'common.pageTitles.tldCatalog' }
              }
            ]
          }
        ]
      },
      {
        // Unknown public path. Returns a real 404 (see useResponseStatus in
        // NotFoundView) rather than the soft 200 the old global catch-all
        // produced by redirecting everything to /login.
        path: ':pathMatch(.*)*',
        name: 'notFound',
        component: () => import('@/views/NotFoundView.vue')
      }
    ]
  },
  {
    // Auth, cart, dashboard and webmail stay unprefixed. They are Disallow'ed
    // in robots.txt and take their language from the account record, so a
    // locale segment would buy nothing. It would also break verify-email,
    // reset-password and invite links that are already in customers' inboxes.
    path: '/',
    meta: {
      layout: LandingLayout
    },
    children: [
      {
        // Bare '/' is normally resolved before the router sees it: nginx and
        // the SSR server negotiate Accept-Language and 302 to /de or /en.
        // This covers in-app navigation to '/' and any direct hit that
        // bypassed that.
        path: '',
        redirect: () => `/${resolveLocale()}`
      },
      {
        path: 'login',
        name: 'login',
        component: () => import('@/views/LoginView.vue'),
        meta: { title: 'ITSH | Login' }
      },
      {
        // OAuth consent. Unprefixed like the rest of this group, and NOT under
        // /dashboard: /authorize hands the browser this URL with a request_id,
        // so it has to be a stable path a third party can be sent to. nginx
        // serves it as the SPA shell (see the `location /oauth` blocks), which
        // is what keeps SSR from rendering a page whose session lives in
        // localStorage. The auth guard covers it through routeRequiresAuth.
        path: 'oauth/consent',
        name: 'oauthConsent',
        component: () => import('@/views/OauthConsentView.vue'),
        // titleKey rather than a literal: the router composes the brand prefix
        // and this page is reached in both languages.
        meta: { titleKey: 'oauth.consent.pageTitle' }
      },
      {
        path: 'register',
        name: 'register',
        component: () => import('@/views/RegisterView.vue'),
        meta: { title: 'ITSH | Registrieren' }
      },
      {
        path: 'verify-email',
        name: 'verifyEmail',
        component: () => import('@/views/VerifyEmailView.vue'),
        beforeEnter: stashQueryToken('verifyEmail'),
        meta: { title: 'ITSH | E-Mail verifizieren', analyticsIgnore: true }
      },
      {
        // Optional so the guard can drop the token from the path; the view
        // reads it from the stash (see @/utils/urlToken).
        path: 'invite/:token?',
        name: 'inviteAccept',
        component: () => import('@/views/InviteAcceptView.vue'),
        beforeEnter: stashParamToken('inviteAccept'),
        meta: { title: 'ITSH | Einladung', analyticsIgnore: true }
      },
      {
        path: 'forgot-password',
        name: 'forgotPassword',
        component: () => import('@/views/ForgotPasswordView.vue'),
        meta: { title: 'ITSH | Passwort vergessen' }
      },
      {
        path: 'reset-password',
        name: 'resetPassword',
        component: () => import('@/views/ResetPasswordView.vue'),
        beforeEnter: stashQueryToken('resetPassword'),
        meta: { title: 'ITSH | Passwort zurücksetzen', analyticsIgnore: true }
      },
      {
        path: 'cart',
        name: 'cart',
        redirect: { name: 'dashboardShoppingCartView' },
        children: [
          {
            path: '',
            name: 'dashboardShoppingCartView',
            component: () => import('@/components/landingPage/ShoppingCart/CartPage.vue'),
            meta: { title: 'ITSH | Warenkorb' }
          },
          {
            path: 'finalizing',
            name: 'dashboardFinalizingView',
            component: () =>
              import('@/components/landingPage/ShoppingCart/ShoppingCartFinalizing.vue'),
            meta: { title: 'ITSH | Zahlungsabschluss' }
          }
        ]
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        meta: {
          layout: DashboardLayout
        },
        redirect: { name: 'dashboardOverview' },
        component: () => import('@/views/DashboardView.vue'),
        children: [
          {
            // Scoped catch-all. Without it an unknown /dashboard/* path falls
            // to the ROOT catch-all, whose redirect runs during matching and
            // therefore before the auth guard: a signed-in user following a
            // stale link lands on the login form with no explanation.
            path: ':pathMatch(.*)*',
            redirect: { name: 'dashboardOverview' }
          },
          {
            path: '',
            name: 'dashboardOverview',
            component: () => import('@/components/dashboard/Dashboard/DashboardHome.vue'),
            meta: { title: 'ITSH | Control Panel' }
          },
          {
            path: 'email/:tab?',
            name: 'dashboardEmail',
            component: () => import('@/components/dashboard/Email/DashboardEmail.vue'),
            meta: { title: 'ITSH | Control Panel - E-Mail' }
          },
          {
            path: 'mail/:id/:tab?',
            name: 'dashboardMailDetail',
            component: () => import('@/components/dashboard/Mail/Detail/MailboxDetail.vue'),
            meta: { title: 'ITSH | Control Panel - Mailbox' }
          },
          {
            path: 'domain',
            name: 'dashboardDomain',
            component: () => import('@/components/dashboard/Domains/DashboardDomain.vue'),
            redirect: { name: 'dashboardDomainOverview' },
            children: [
              {
                path: '',
                name: 'dashboardDomainOverview',
                component: () =>
                  import('@/components/dashboard/Domains/Domain/DomainOverview.vue'),
                meta: { title: 'ITSH | Control Panel - Domain' }
              },
              {
                path: 'register',
                name: 'dashboardDomainRegister',
                component: () =>
                  import('@/components/dashboard/Domains/Register/DomainRegisterPage.vue'),
                meta: { title: 'ITSH | Control Panel - Domain registrieren' }
              },
              {
                path: ':id',
                name: 'dashboardDomainVerwaltung',
                component: () =>
                  import('@/components/dashboard/Domains/Domain/DomainVerwaltung.vue'),
                meta: { title: 'ITSH | Control Panel - Domain' }
              },
              {
                path: 'contacts',
                name: 'dashboardDomainContacts',
                component: () =>
                  import('@/components/dashboard/Domains/Contacts/ContactsOverview.vue'),
                meta: { title: 'ITSH | Control Panel - Kontakte' }
              },
              {
                path: 'transfers',
                name: 'dashboardDomainTransfers',
                component: () =>
                  import('@/components/dashboard/Domains/Transfers/TransferOutOverview.vue'),
                meta: { title: 'ITSH | Control Panel - Transfer-Out Anfragen' }
              },
              {
                path: 'internal-transfers',
                name: 'dashboardDomainInternalTransfers',
                component: () =>
                  import('@/components/dashboard/Domains/InternalTransfers/InternalTransfersOverview.vue'),
                meta: { title: 'ITSH | Control Panel - Interne Transfers' }
              }
            ]
          },
          {
            path: 'account/:tab',
            name: 'dashboardAccount',
            component: () => import('@/components/dashboard/Account/DashboardAccount.vue'),
            meta: { title: 'ITSH | Control Panel - Account' }
          },
          {
            path: 'avv',
            name: 'dashboardAvv',
            component: () => import('@/components/dashboard/Account/AccountPageAvv.vue'),
            meta: { title: 'ITSH | Control Panel - AVV' }
          },
          {
            path: 'sub-accounts',
            name: 'dashboardSubAccounts',
            component: () => import('@/components/dashboard/SubAccounts/SubAccountsHome.vue'),
            meta: { title: 'ITSH | Control Panel - Sub-Accounts' }
          },
          {
            path: 'packet',
            name: 'dashboardPacket',
            component: () => import('@/components/dashboard/Packet/DashboardPacket.vue'),
            meta: { title: 'ITSH | Control Panel - Pakete' }
          },
          {
            // The withdrawal function of § 356a BGB. The Widerrufsbelehrung
            // names this URL, so the path must not change.
            path: 'withdraw',
            name: 'dashboardWithdraw',
            component: () => import('@/components/dashboard/Packet/DashboardWithdraw.vue'),
            meta: { title: 'ITSH | Control Panel - Vertrag widerrufen' }
          },
          {
            path: 'storage',
            name: 'dashboardStorage',
            component: () => import('@/components/dashboard/Storage/DashboardStorage.vue'),
            meta: { title: 'ITSH | Control Panel - Object Storage' }
          },
          {
            path: 'kubernetes',
            name: 'dashboardKubernetes',
            component: () => import('@/components/dashboard/Kubernetes/DashboardKubernetes.vue'),
            meta: { title: 'ITSH | Control Panel - Kubernetes' }
          },
          {
            path: 'vserver',
            name: 'dashboardVServer',
            component: () => import('@/components/dashboard/VServer/DashboardVServer.vue'),
            meta: { title: 'ITSH | Control Panel - VServer' }
          },
          {
            path: 'hosting',
            name: 'dashboardHosting',
            component: () => import('@/components/dashboard/Hosting/DashboardHosting.vue'),
            meta: { title: 'ITSH | Control Panel - Webhosting' }
          },
          {
            path: 'hosting/:name',
            name: 'hostingDetail',
            component: () => import('@/components/dashboard/Hosting/HostingSiteDetail.vue'),
            meta: { title: 'ITSH | Control Panel - Webhosting' }
          },
          {
            path: 'registry',
            name: 'dashboardRegistry',
            component: () => import('@/components/dashboard/Registry/DashboardRegistry.vue'),
            meta: { title: 'ITSH | Control Panel - Container Registry' }
          },
          {
            path: 'admin',
            name: 'dashboardAdmin',
            redirect: { name: 'dashboardAdminHome' },
            component: () => import('@/components/dashboard/Admin/DashboardAdmin.vue'),
            meta: { title: 'ITSH | admin' },
            children: [
              {
                path: '',
                name: 'dashboardAdminHome',
                component: () => import('@/components/dashboard/Admin/AdminHome/AdminHome.vue'),
                meta: { title: 'ITSH | admin' }
              },
              {
                path: 'product',
                name: 'dashboardAdminProduct',
                component: () =>
                  import(
                    '@/components/dashboard/Admin/AdminProduct/ProductManagementHome.vue'
                  ),
                meta: { title: 'ITSH | admin - Produkte' }
              },
              {
                path: 'product/:id',
                name: 'dashboardAdminProductEdit',
                component: () =>
                  import('@/components/dashboard/Admin/AdminProduct/ProductEdit.vue'),
                meta: { title: (to: any) => `ITSH | admin - Produkt-${to.params.id}` }
              },
              {
                path: 'user-management',
                name: 'dashboardAdminUserManagement',
                component: () =>
                  import(
                    '@/components/dashboard/Admin/AdminUserManagement/UserManagementHome.vue'
                  ),
                meta: { title: 'ITSH | admin - UserManagement' }
              },
              {
                path: 'user/:id',
                name: 'dashboardAdminUserDetail',
                component: () =>
                  import(
                    '@/components/dashboard/Admin/AdminUserManagement/UserDetailPage.vue'
                  ),
                meta: { title: (to: any) => `ITSH | admin - User ${to.params.id}` }
              },
              {
                path: 'feature-management',
                name: 'dashboardAdminFeatureManagement',
                component: () =>
                  import(
                    '@/components/dashboard/Admin/AdminFeatureManagement/FeatureManagementHome.vue'
                  ),
                meta: { title: 'ITSH | admin - Feature Flags' }
              },
              {
                path: 'roles',
                name: 'dashboardAdminRoles',
                component: () =>
                  import(
                    '@/components/dashboard/Admin/AdminRoles/RolesHome.vue'
                  ),
                meta: { title: 'ITSH | admin - Rollen' }
              },
              {
                path: 'migration',
                name: 'dashboardAdminMigration',
                component: () =>
                  import(
                    '@/components/dashboard/Admin/AdminMigration/MigrationHome.vue'
                  ),
                meta: { title: 'ITSH | admin - Domain-Migration' }
              },
              {
                path: 'account-splits',
                name: 'dashboardAdminAccountSplit',
                component: () =>
                  import(
                    '@/components/dashboard/Admin/AdminAccountSplit/AccountSplitHome.vue'
                  ),
                meta: {
                  title: 'ITSH | admin - Account-Splits',
                  requiresAuth: true,
                  requiresPermission: 'account:split'
                }
              },
              {
                path: 'account-splits/new',
                name: 'dashboardAdminAccountSplitNew',
                component: () =>
                  import(
                    '@/components/dashboard/Admin/AdminAccountSplit/AccountSplitBuilder.vue'
                  ),
                meta: {
                  title: 'ITSH | admin - Account-Split anlegen',
                  requiresAuth: true,
                  requiresPermission: 'account:split'
                }
              },
              {
                path: 'account-splits/:id',
                name: 'dashboardAdminAccountSplitDetail',
                component: () =>
                  import(
                    '@/components/dashboard/Admin/AdminAccountSplit/AccountSplitDetail.vue'
                  ),
                meta: {
                  title: (to: any) => `ITSH | admin - Account-Split #${to.params.id}`,
                  requiresAuth: true,
                  requiresPermission: 'account:split'
                }
              },
              {
                path: 'fulfillment',
                name: 'dashboardAdminFulfillment',
                component: () =>
                  import(
                    '@/components/dashboard/Admin/AdminFulfillment/FulfillmentHome.vue'
                  ),
                meta: { title: 'ITSH | admin - Fulfillment' }
              },
              {
                path: 'webhook',
                name: 'dashboardAdminWebhook',
                component: () =>
                  import(
                    '@/components/dashboard/Admin/AdminWebhook/WebhookHome.vue'
                  ),
                meta: { title: 'ITSH | admin - Webhooks' }
              },
              {
                path: 'compliance',
                name: 'dashboardAdminCompliance',
                redirect: { name: 'dashboardAdminComplianceDashboard' },
                children: [
                  {
                    path: '',
                    name: 'dashboardAdminComplianceDashboard',
                    component: () =>
                      import(
                        '@/components/dashboard/Admin/AdminCompliance/ComplianceHome.vue'
                      ),
                    meta: { title: 'ITSH | admin - Compliance' }
                  },
                  {
                    path: 'disclosure',
                    name: 'dashboardAdminDisclosure',
                    component: () =>
                      import(
                        '@/components/dashboard/Admin/AdminCompliance/Disclosure/DisclosureList.vue'
                      ),
                    meta: { title: 'ITSH | admin - Offenlegung' }
                  },
                  {
                    path: 'abuse',
                    name: 'dashboardAdminAbuse',
                    component: () =>
                      import(
                        '@/components/dashboard/Admin/AdminCompliance/Abuse/AbuseList.vue'
                      ),
                    meta: { title: 'ITSH | admin - Missbrauch' }
                  },
                  {
                    path: 'abuse/:id',
                    name: 'dashboardAdminAbuseDetail',
                    component: () =>
                      import(
                        '@/components/dashboard/Admin/AdminCompliance/Abuse/AbuseDetail.vue'
                      ),
                    meta: { title: 'ITSH | admin - Missbrauchsfall' }
                  },
                  {
                    path: 'suspended-domains',
                    name: 'dashboardAdminSuspendedDomains',
                    component: () =>
                      import(
                        '@/components/dashboard/Admin/AdminCompliance/SuspendedDomains/SuspendedDomainsList.vue'
                      ),
                    meta: { title: 'ITSH | admin - Gesperrte Domains' }
                  },
                  {
                    path: 'handle-revalidation',
                    name: 'dashboardAdminHandleRevalidation',
                    component: () =>
                      import(
                        '@/components/dashboard/Admin/AdminCompliance/HandleRevalidation/HandleRevalidationList.vue'
                      ),
                    meta: { title: 'ITSH | admin - Handle-Revalidierung' }
                  }
                ]
              },
              {
                path: 'domains',
                name: 'dashboardAdminDomains',
                component: () =>
                  import(
                    '@/components/dashboard/Admin/AdminDomains/AdminDomainsList.vue'
                  ),
                meta: { title: 'ITSH | admin - Alle Domains' }
              },
              {
                path: 'domains/:id',
                name: 'dashboardAdminDomainDetail',
                component: () =>
                  import(
                    '@/components/dashboard/Admin/AdminDomains/AdminDomainDetail.vue'
                  ),
                meta: { title: 'ITSH | admin - Domain Details' }
              },
              {
                path: 'handles',
                name: 'dashboardAdminHandles',
                component: () =>
                  import(
                    '@/components/dashboard/Admin/AdminHandles/AdminHandlesList.vue'
                  ),
                meta: { title: 'ITSH | admin - Alle Handles' }
              },
              {
                path: 'handles/:id',
                name: 'dashboardAdminHandleDetail',
                component: () =>
                  import(
                    '@/components/dashboard/Admin/AdminHandles/AdminHandleDetail.vue'
                  ),
                meta: { title: 'ITSH | admin - Handle Details' }
              },
              {
                path: 'featured-tlds',
                name: 'dashboardAdminFeaturedTLDs',
                component: () =>
                  import(
                    '@/components/dashboard/Admin/AdminFeaturedTLDs/FeaturedTLDHome.vue'
                  ),
                meta: { title: 'ITSH | admin - Featured TLDs' }
              },
              {
                path: 'mail',
                name: 'dashboardAdminMail',
                redirect: { name: 'dashboardAdminMailHome' },
                children: [
                  {
                    path: '',
                    name: 'dashboardAdminMailHome',
                    component: () =>
                      import(
                        '@/components/dashboard/Admin/AdminMail/AdminMailHome.vue'
                      ),
                    meta: { title: 'ITSH | admin - Mail' }
                  },
                  {
                    path: 'accounts',
                    name: 'dashboardAdminMailAccounts',
                    component: () =>
                      import(
                        '@/components/dashboard/Admin/AdminMail/AdminMailAccountsList.vue'
                      ),
                    meta: { title: 'ITSH | admin - Mail Accounts' }
                  },
                  {
                    path: 'server',
                    name: 'dashboardAdminMailServer',
                    component: () =>
                      import(
                        '@/components/dashboard/Admin/AdminMail/AdminMailServerStats.vue'
                      ),
                    meta: { title: 'ITSH | admin - Mail Server' }
                  },
                  {
                    path: 'messages',
                    name: 'dashboardAdminMailMessages',
                    component: () =>
                      import(
                        '@/components/dashboard/Admin/AdminMail/AdminMailMessages.vue'
                      ),
                    meta: { title: 'ITSH | admin - Mail Messages' }
                  },
                  {
                    path: 'send-limits',
                    name: 'dashboardAdminMailSendLimits',
                    component: () =>
                      import(
                        '@/components/dashboard/Admin/AdminMail/AdminMailSendLimits.vue'
                      ),
                    meta: { title: 'ITSH | admin - Send Limits' }
                  }
                ]
              },
              {
                path: 'storage',
                name: 'dashboardAdminStorage',
                component: () =>
                  import(
                    '@/components/dashboard/Admin/AdminStorage/AdminStorageBuckets.vue'
                  ),
                meta: { title: 'ITSH | admin - Storage' }
              },
              {
                path: 'registry',
                name: 'dashboardAdminRegistry',
                component: () =>
                  import(
                    '@/components/dashboard/Admin/AdminRegistry/AdminRegistryHome.vue'
                  ),
                meta: { title: 'ITSH | admin - Registry' }
              },
              {
                path: 'registry/:id',
                name: 'dashboardAdminRegistryDetail',
                component: () =>
                  import(
                    '@/components/dashboard/Admin/AdminRegistry/AdminRegistryDetail.vue'
                  ),
                meta: { title: 'ITSH | admin - Registry Details' }
              },
              {
                path: 'kubernetes',
                name: 'dashboardAdminK8s',
                redirect: { name: 'dashboardAdminK8sHome' },
                children: [
                  {
                    path: '',
                    name: 'dashboardAdminK8sHome',
                    component: () =>
                      import(
                        '@/components/dashboard/Admin/AdminKubernetes/AdminK8sHome.vue'
                      ),
                    meta: { title: 'ITSH | admin - Kubernetes' }
                  },
                  {
                    path: 'usage',
                    name: 'dashboardAdminK8sUsage',
                    component: () =>
                      import(
                        '@/components/dashboard/Admin/AdminKubernetes/AdminK8sUsageOverview.vue'
                      ),
                    meta: { title: 'ITSH | admin - K8s Nutzung' }
                  },
                  {
                    path: 'domains',
                    name: 'dashboardAdminK8sDomains',
                    component: () =>
                      import(
                        '@/components/dashboard/Admin/AdminKubernetes/AdminK8sDomains.vue'
                      ),
                    meta: { title: 'ITSH | admin - K8s Domains' }
                  },
                  {
                    path: ':id',
                    name: 'dashboardAdminK8sTenantDetail',
                    component: () =>
                      import(
                        '@/components/dashboard/Admin/AdminKubernetes/AdminK8sTenantDetail.vue'
                      ),
                    meta: { title: 'ITSH | admin - K8s Tenant' }
                  }
                ]
              },
              {
                path: 'vserver',
                name: 'dashboardAdminVServer',
                component: () =>
                  import('@/components/dashboard/Admin/AdminVServer/AdminVServerHome.vue'),
                meta: { title: 'ITSH | admin - VServer' }
              },
              {
                path: 'vserver/ip-pool',
                name: 'dashboardAdminVServerIpPool',
                component: () =>
                  import('@/components/dashboard/Admin/AdminVServer/AdminVServerIpPool.vue'),
                meta: { title: 'ITSH | admin - IP Pool' }
              },
              {
                path: 'vserver/import',
                name: 'dashboardAdminVServerImport',
                component: () =>
                  import('@/components/dashboard/Admin/AdminVServer/AdminVServerImportHome.vue'),
                meta: { title: 'ITSH | admin - VServer Import' }
              },
              {
                path: 'vserver/templates',
                name: 'dashboardAdminVServerTemplates',
                component: () =>
                  import('@/components/dashboard/Admin/AdminVServer/AdminVServerTemplates.vue'),
                meta: { title: 'ITSH | admin - VServer Templates' }
              },
              {
                path: 'vserver/:id',
                name: 'dashboardAdminVServerDetail',
                component: () =>
                  import('@/components/dashboard/Admin/AdminVServer/AdminVServerDetail.vue'),
                meta: { title: 'ITSH | admin - VServer Detail' }
              },
              {
                path: 'hosting',
                name: 'dashboardAdminHosting',
                component: () =>
                  import(
                    '@/components/dashboard/Admin/AdminHosting/AdminHostingList.vue'
                  ),
                meta: { title: 'ITSH | admin - Webhosting' }
              },
              {
                path: 'hosting/:id',
                name: 'dashboardAdminHostingDetail',
                component: () =>
                  import(
                    '@/components/dashboard/Admin/AdminHosting/AdminHostingDetail.vue'
                  ),
                meta: { title: 'ITSH | admin - Webhosting Detail' }
              }
            ]
          },
          {
            path: 'invoice',
            name: 'dashboardInvoice',
            component: () => import('@/components/dashboard/Invoice/DashboardInvoice.vue'),
            meta: { title: 'ITSH | Control Panel - Rechnungen' }
          },
          {
            path: 'shop',
            name: 'dashboardShop',
            redirect: { name: 'dashboardShopHome' },
            children: [
              {
                path: '',
                name: 'dashboardShopHome',
                component: () => import('@/components/dashboard/Shop/ShopPage.vue'),
                meta: { title: 'ITSH | Control Panel - Shop' }
              },
              {
                path: 'webhosting',
                name: 'dashboardShopWebhosting',
                component: () => import('@/components/dashboard/Shop/ShopWebhosting.vue'),
                meta: { title: 'ITSH | Control Panel - Webhosting' }
              },
              {
                path: 'kubernetes',
                name: 'dashboardShopKubernetes',
                component: () => import('@/components/dashboard/Shop/ShopKubernetes.vue'),
                meta: { title: 'ITSH | Control Panel - Kubernetes' }
              },
              {
                path: 'vserver',
                name: 'dashboardShopVServer',
                component: () => import('@/components/dashboard/Shop/ShopVServer.vue'),
                meta: { title: 'ITSH | Control Panel - VServer' }
              },
              {
                path: 'email',
                name: 'dashboardShopEmail',
                component: () => import('@/components/dashboard/Shop/ShopEmail.vue'),
                meta: { title: 'ITSH | Control Panel - E-Mail Hosting' }
              }
            ]
          },
          {
            path: 'cart',
            name: 'dashboardCart',
            component: () => import('@/components/dashboard/Cart/CartPage.vue'),
            meta: { title: 'ITSH | Control Panel - Warenkorb' }
          },
          {
            path: 'cart/finalizing',
            name: 'dashboardCheckoutSuccess',
            component: () =>
              import('@/components/dashboard/Cart/CheckoutFinalizingPage.vue'),
            meta: { title: 'ITSH | Control Panel - Zahlungsabschluss' }
          }
        ]
      },
      {
        path: 'webmail',
        name: 'webmail',
        component: () => import('@/views/WebmailLoginView.vue'),
        meta: { title: 'ITSH | Webmail' }
      },
      {
        path: 'webmail/dashboard/:tab?',
        name: 'webmailDashboard',
        component: () => import('@/views/WebmailDashboardView.vue'),
        meta: {
          layout: WebmailLayout,
          title: 'ITSH | Webmail'
        }
      },
      {
        path: 'webmail/domain/:domainId/:tab?',
        name: 'webmailDomain',
        component: () => import('@/views/WebmailDomainView.vue'),
        meta: {
          layout: WebmailLayout,
          title: 'ITSH | Webmail'
        }
      },
      {
        path: ':pathMatch(.*)*',
        redirect: { name: 'login' }
      }
    ]
  }
]

/** Every named route under a record tree, at any depth. */
function collectNames(records: readonly RouteRecordRaw[], out = new Set<string>()): Set<string> {
  for (const r of records) {
    if (typeof r.name === 'string') out.add(r.name)
    if (r.children) collectNames(r.children, out)
  }
  return out
}

/**
 * Names of routes that live under the locale parent.
 *
 * Found by path rather than by array index, and collected RECURSIVELY: `blog`
 * and `products` are branch records, and their children (blogIndex, blogPost,
 * kubernetes, domainsSearch, ...) are what the footer, the nav dropdown and
 * global search actually link to. A one-level scan covers under half of them.
 */
const PUBLIC_ROUTE_NAMES: ReadonlySet<string> = collectNames(
  routes.find((r) => r.path.startsWith('/:locale'))?.children ?? []
)

/**
 * Makes named public routes resolvable and navigable from anywhere.
 *
 * Public routes sit under `/:locale(de|en)`, so resolving one by name from a
 * location that carries no locale param throws `Missing required param
 * "locale"`. That is every dashboard, cart, webmail and auth page.
 *
 * Both resolve AND push/replace must be wrapped. vue-router's internal
 * `pushWithRedirect` calls a module-local `resolve` closure rather than
 * `router.resolve`, and `RouterLink.navigate` hands `router.push` the RAW
 * target after `guardEvent` has already cancelled the browser navigation. Wrap
 * only resolve and every link renders a correct href, cancels the click, and
 * then throws into the error handler: a link that lies, which is worse than
 * the missing link it replaced.
 *
 * NOT covered: a location returned from a navigation guard (or passed to
 * `next()`). That re-enters vue-router's internal redirect recursion, which
 * never touches the public methods, so such a target must carry the locale
 * param itself. Every guard here currently redirects to `login` or
 * `dashboardOverview`, both unprefixed, so nothing is affected today.
 */
function withLocaleResolution(router: Router): void {
  const injectLocale = <T>(to: T): T => {
    if (!to || typeof to !== 'object' || !('name' in to)) return to
    const named = to as { name?: unknown; params?: Record<string, unknown> }
    if (typeof named.name !== 'string' || !PUBLIC_ROUTE_NAMES.has(named.name)) return to
    if (named.params?.locale) return to
    // The CURRENT ROUTE is the authority, not the i18n accessor: during SSR
    // the accessor is null by contract, and every link on a /de/ page would
    // otherwise resolve to the other language.
    const fromRoute = router.currentRoute.value.params.locale
    const locale =
      typeof fromRoute === 'string' && availableLocales.includes(fromRoute as Locale)
        ? fromRoute
        : resolveLocale()
    return { ...to, params: { ...named.params, locale } } as T
  }

  const innerResolve = router.resolve.bind(router)
  const innerPush = router.push.bind(router)
  const innerReplace = router.replace.bind(router)

  router.resolve = ((to: Parameters<Router['resolve']>[0], current?: Parameters<Router['resolve']>[1]) =>
    innerResolve(injectLocale(to), current)) as Router['resolve']
  router.push = ((to: Parameters<Router['push']>[0]) => innerPush(injectLocale(to))) as Router['push']
  router.replace = ((to: Parameters<Router['replace']>[0]) => innerReplace(injectLocale(to))) as Router['replace']
}

export function createAppRouter(isSSR = false) {
  const router = createRouter({
    history: isSSR ? createMemoryHistory(import.meta.env.BASE_URL) : createWebHistory(import.meta.env.BASE_URL),
    // Scroll handling. Two pieces:
    //   1. Wait one tick before scrolling so the new route's DOM is mounted —
    //      otherwise the scroll fires against the OLD page and is then
    //      effectively undone when the new content replaces it.
    //   2. Use instant (default) behavior for new-route navigation. `smooth`
    //      animates during render; layout shifts mid-animation can cancel it
    //      and leave the page mid-scroll. savedPosition (back/forward) keeps
    //      smooth because that's a within-document re-scroll, not a new page.
    scrollBehavior(to, from, savedPosition) {
      return new Promise((resolve) => {
        setTimeout(() => {
          if (savedPosition) {
            resolve({ ...savedPosition, behavior: 'smooth' })
          } else if (to.path === from.path) {
            // Same-page navigation (query-only change, e.g. search params or
            // tab state): keep the current scroll position. Pages that need
            // to move the viewport (e.g. domain search scrolling to results)
            // do so themselves — a top-reset here would race and undo it.
            resolve(false)
          } else {
            resolve({ top: 0, left: 0 })
          }
        }, 0)
      })
    },
    routes,
    // Without this '/DE/agb' also matches, producing a duplicate-content URL
    // and a third nginx cache key for the same page.
    sensitive: true
  })

  withLocaleResolution(router)

  // Take over scroll management from the browser. Default
  // history.scrollRestoration='auto' restores the source URL's scroll
  // position on pushState in some browsers — fighting our scrollBehavior.
  // Setting it to 'manual' makes scrollBehavior the single source of truth.
  if (isClient && typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual'
  }

  // Authentication guard - skip on server (SSR will return SPA shell for dashboard routes)
  // Client-only in effect: getActiveI18n() is null during SSR by contract, so
  // this is a no-op there. SSR gets its locale from createApp(true, locale) in
  // entry-server.ts, which sets it before the first component renders. This
  // guard exists for client-side navigation between /de and /en.
  router.beforeEach((to) => {
    const seg = to.params.locale
    const next = Array.isArray(seg) ? seg[0] : seg
    if (!next || !availableLocales.includes(next as Locale)) return
    const i18n = getActiveI18n()
    if (i18n && i18n.global.locale.value !== next) {
      i18n.global.locale.value = next as Locale
    }
  })

  // Older domain search links carry transfer codes: the palette's `?q=` held
  // the raw query, and `?query=` held "domain:code" lines. Rewritten to the
  // names before the page, analytics or a version reload see them. The codes
  // are not stashed: SSR requests share that module-level state.
  router.beforeEach((to) => {
    if (to.name !== 'domainsSearch') return
    const raw = [to.query.q ?? to.query.query]
      .flat()
      .filter((v): v is string => typeof v === 'string')
      .join('\n')
    if (!('q' in to.query) && !raw.includes(':')) return
    const names = validSearchNames(raw).join('\n').toLowerCase()
    return {
      name: to.name,
      params: to.params,
      hash: to.hash,
      query: names ? { query: names } : {},
      replace: true
    }
  })

  if (isClient) {
    // Dirty form guard - warn before navigating away from forms with unsaved changes
    router.beforeEach((to, from) => {
      // Only check when navigating away from dashboard routes
      if (!from.path.startsWith('/dashboard')) {
        return true
      }

      // Check for dirty forms
      const versionStore = useVersionStore()
      if (!versionStore.hasDirtyForm) {
        return true
      }

      // Dispatch event for DashboardLayout to show confirmation dialog
      const event = new CustomEvent<DirtyFormNavigationEvent>(DIRTY_FORM_NAVIGATION_EVENT, {
        detail: { to, from }
      })
      window.dispatchEvent(event)

      // Cancel navigation - dialog will handle redirect if user confirms
      return false
    })

    // Auto-reload on navigation when a new version is available
    // This ensures users get fresh chunks instead of 404ing on stale hashes
    router.beforeEach((to) => {
      const versionStore = useVersionStore()
      if (versionStore.isUpdateAvailable && !versionStore.hasDirtyForm) {
        window.location.href = to.fullPath
        return false
      }
    })

    router.beforeEach((to, _from, next) => {
      const requiresAuth = routeRequiresAuth(to.path)
      const requiresAdmin = to.path.startsWith('/dashboard/admin')

      if (requiresAuth) {
        const storage = safeLocalStorage()
        const token = storage?.getItem('jwt')
        if (!token) {
          next(loginRouteWithRedirect(to.fullPath))
          return
        }

        try {
          const parsed = JwtParser(token)
          const unixNow = Math.floor(Date.now() / 1000)

          if (!parsed || typeof parsed.exp !== 'number' || unixNow > parsed.exp) {
            const jwtStore = useJwtStore()
            jwtStore.removeJwt()
            next(loginRouteWithRedirect(to.fullPath))
            return
          }

          // Check admin access using RBAC roles array (with fallback to deprecated role field)
          const isAdmin = parsed.roles?.includes('admin') ||
                         parsed.roles?.includes('super_admin') ||
                         parsed.role === 'admin'
          if (requiresAdmin && !isAdmin) {
            next({ name: 'dashboardOverview' })
            return
          }

          // Check meta.requiresPermission - global permission check
          const requiredPermission = to.meta?.requiresPermission as string | undefined
          if (requiredPermission) {
            const userPerms = parsed.perms || []
            if (!userPerms.includes(requiredPermission) && !isAdmin) {
              next({ name: 'dashboardOverview' })
              return
            }
          }

          // Check meta.requiresDomainPermission - domain-scoped permission check
          // Requires route param 'id' to be the domain ID
          const requiredDomainPermission = to.meta?.requiresDomainPermission as string | undefined
          if (requiredDomainPermission && to.params.id) {
            const userPerms = parsed.perms || []
            // For domain-scoped, check if user has the global permission OR is admin
            // More granular domain-level checks happen in components via usePermissions
            if (!userPerms.includes(requiredDomainPermission) && !isAdmin) {
              next({ name: 'dashboardOverview' })
              return
            }
          }

        } catch {
          next(loginRouteWithRedirect(to.fullPath))
          return
        }
      }

      next()
    })

    router.afterEach((to) => {
      const meta = to.meta as any
      let nextTitle: string
      if (meta?.titleKey) {
        // The brand prefix is composed here, not stored in the locale files.
        // vue-i18n treats `|` as the plural delimiter, so a message like
        // 'ITSH | Produkte' compiles to a plural node and t() without a count
        // returns only the first branch, i.e. bare 'ITSH'.
        nextTitle = `ITSH | ${translate(meta.titleKey)}`
      } else if (typeof meta?.title === 'function') {
        nextTitle = meta.title(to)
      } else if (typeof meta?.title === 'string') {
        nextTitle = meta.title
      } else {
        nextTitle = 'ITSH'
      }
      if (!isProdEnv()) {
        nextTitle = 'DEV - ' + nextTitle
      }
      document.title = nextTitle
    })

    // A lazy route component whose chunk a deployment has replaced reloads onto
    // the target URL, so the navigation the user started finishes on the new build.
    router.onError((error, to) => {
      recoverFromChunkError(error, to.fullPath)
    })
  }

  return router
}

/**
 * The router instance the app was actually mounted with.
 *
 * This used to be a default export that called createAppRouter() a second time.
 * That instance was never passed to app.use(), so it never ran an initial
 * navigation: its currentRoute stayed at START_LOCATION ('/') forever and
 * push() only rewrote window.history without the mounted app ever noticing.
 * Every guard written against it silently did nothing.
 *
 * Components must use useRouter() from vue-router. This accessor exists only
 * for modules that have no setup scope (stores, the axios interceptor).
 * Deliberately client-only: on the server every request builds its own router,
 * and a module-level slot would leak one request's router into the next.
 */
let activeRouter: Router | null = null

export function setActiveRouter(router: Router) {
  activeRouter = router
}

/** Returns null during SSR and before the app is created. Callers must handle it. */
export function getActiveRouter(): Router | null {
  return activeRouter
}
